import Lenis from '@studio-freight/lenis';
import * as CookieConsent from 'vanilla-cookieconsent';
import { inject } from '@vercel/analytics';
import 'vanilla-cookieconsent/dist/cookieconsent.css';
import '../css/cookieconsent-theme.css';

inject();

// Initialize Lenis smooth scroll (needed early so the consent banner can pause it)
const lenis = new Lenis({
  duration: 1.2,
  easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
  orientation: 'vertical',
  gestureOrientation: 'vertical',
  smoothWheel: true,
  wheelMultiplier: 1,
  smoothTouch: false,
  touchMultiplier: 2,
  infinite: false,
});

function raf(time) {
  lenis.raf(time);
  requestAnimationFrame(raf);
}

requestAnimationFrame(raf);

// Push the user's cookie choices to GTM through Consent Mode signals
function syncConsentMode() {
  const analytics = CookieConsent.acceptedCategory('analytics');
  const marketing = CookieConsent.acceptedCategory('marketing');

  if (typeof gtag === 'function') {
    gtag('consent', 'update', {
      analytics_storage: analytics ? 'granted' : 'denied',
      ad_storage: marketing ? 'granted' : 'denied',
      ad_user_data: marketing ? 'granted' : 'denied',
      ad_personalization: marketing ? 'granted' : 'denied'
    });
  }
}

CookieConsent.run({
  autoShow: false,
  disablePageInteraction: true,
  guiOptions: {
    consentModal: { layout: 'box wide', position: 'bottom center', equalWeightButtons: true },
    preferencesModal: { layout: 'box', equalWeightButtons: true }
  },
  onFirstConsent: syncConsentMode,
  onConsent: syncConsentMode,
  onChange: syncConsentMode,
  onModalShow: () => lenis.stop(),
  onModalHide: () => lenis.start(),
  categories: {
    necessary: { enabled: true, readOnly: true },
    analytics: {},
    marketing: {}
  },
  language: {
    default: 'en',
    translations: {
      en: {
        consentModal: {
          title: 'We use cookies',
          description: 'We use cookies to measure our audience and improve your experience. You decide what to allow.',
          acceptAllBtn: 'Accept all',
          acceptNecessaryBtn: 'Reject all',
          showPreferencesBtn: 'Manage preferences'
        },
        preferencesModal: {
          title: 'Cookie preferences',
          acceptAllBtn: 'Accept all',
          acceptNecessaryBtn: 'Reject all',
          savePreferencesBtn: 'Save my choices',
          closeIconLabel: 'Close',
          sections: [
            {
              title: 'Strictly necessary',
              description: 'Required for the website to function. Always active.',
              linkedCategory: 'necessary'
            },
            {
              title: 'Analytics',
              description: 'Anonymous traffic statistics that help us understand how the site is used.',
              linkedCategory: 'analytics'
            },
            {
              title: 'Marketing',
              description: 'Used to measure advertising performance and show relevant content.',
              linkedCategory: 'marketing'
            }
          ]
        }
      }
    }
  }
});

function showCookieBannerIfNeeded() {
  if (!CookieConsent.validConsent()) {
    CookieConsent.show();
  }
}

// Age Gate
const ageGate = document.getElementById('ageGate');

if (sessionStorage.getItem('ageVerified')) {
  ageGate.classList.add('hidden');
  showCookieBannerIfNeeded();
} else {
  document.body.style.overflow = 'hidden';
}

document.getElementById('ageYes').addEventListener('click', () => {
  sessionStorage.setItem('ageVerified', 'true');
  ageGate.classList.add('hidden');
  document.body.style.overflow = '';
  showCookieBannerIfNeeded();
});

document.getElementById('ageNo').addEventListener('click', () => {
  window.location.href = 'https://www.google.com';
});

// Smooth scrolling for navigation links with Lenis
document.querySelectorAll('a[href^="#"]').forEach(anchor => {
  anchor.addEventListener('click', function (e) {
    e.preventDefault();
    const target = document.querySelector(this.getAttribute('href'));
    if (target) {
      lenis.scrollTo(target, {
        offset: 0,
        duration: 1.5,
      });
    }
  });
});

// Add scroll effect to header - minimize and rearrange on scroll
const header = document.querySelector('header');
const scrollThreshold = 100;

lenis.on('scroll', ({ scroll }) => {
  if (scroll > scrollThreshold) {
    header.classList.add('scrolled');
  } else {
    header.classList.remove('scrolled');
  }

  // Update active section indicator
  updateActiveSection();
});

// Active section indicator
function updateActiveSection() {
  const sections = document.querySelectorAll('section[id]');
  const navLinks = document.querySelectorAll('.nav-links a[href^="#"]');

  let currentSection = '';

  sections.forEach(section => {
    const sectionTop = section.offsetTop;
    const sectionHeight = section.clientHeight;
    const scrollPosition = window.pageYOffset + 200; // Offset for header

    if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
      currentSection = section.getAttribute('id');
    }
  });

  navLinks.forEach(link => {
    link.classList.remove('active');
    if (link.getAttribute('href') === `#${currentSection}`) {
      link.classList.add('active');
    }
  });
}

// Initial call
updateActiveSection();


// Perfect Serves - horizontal accordion
const serveCards = document.querySelectorAll('.serve-card');

serveCards.forEach(card => {
  card.addEventListener('click', () => {
    const isActive = card.classList.contains('active');

    // Reset all
    serveCards.forEach(c => {
      c.classList.remove('active', 'collapsed');
    });

    if (!isActive) {
      card.classList.add('active');
      serveCards.forEach(c => {
        if (c !== card) c.classList.add('collapsed');
      });
    }
  });
});

// Back to top
const backToTop = document.getElementById('backToTop');

lenis.on('scroll', ({ scroll }) => {
  if (scroll > 500) {
    backToTop.classList.add('visible');
  } else {
    backToTop.classList.remove('visible');
  }
});

backToTop.addEventListener('click', () => {
  lenis.scrollTo(0, { duration: 1.5 });
});

// Lightbox for mosaic images
const lightbox = document.getElementById('lightbox');
const lightboxImg = lightbox.querySelector('img');

document.querySelectorAll('.mosaic-item img').forEach(img => {
  img.style.cursor = 'pointer';
  img.addEventListener('click', () => {
    lightboxImg.src = img.src;
    lightboxImg.alt = img.alt;
    lightbox.classList.add('active');
  });
});

lightbox.addEventListener('click', () => {
  lightbox.classList.remove('active');
});

// Contact form (Web3Forms)
const contactForm = document.querySelector('.contact-form');
const contactFeedback = document.getElementById('contactFeedback');

if (contactForm) {
  contactForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    contactFeedback.textContent = 'Sending...';
    contactFeedback.className = 'contact-feedback';

    const formData = new FormData(contactForm);
    try {
      const response = await fetch(contactForm.action, {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
      });
      const data = await response.json();
      if (response.ok && data.success) {
        contactFeedback.textContent = 'Thanks, your message is on its way.';
        contactFeedback.classList.add('success');
        contactForm.reset();
      } else {
        contactFeedback.textContent = data.message || 'Something went wrong. Please try again.';
        contactFeedback.classList.add('error');
      }
    } catch (err) {
      contactFeedback.textContent = 'Network error. Please try again later.';
      contactFeedback.classList.add('error');
    }
  });
}

console.log('Drebbel Gin website loaded successfully');
